import { CardShell } from '@atlas/ui-components'
import { AddAxisButton } from './AddAxisButton'
import { AxisControl } from './AxisControl'
import { DEFAULT_AXES } from './classify-config'
import type { ClassificationTuple } from './staging'
import type { PanelAxis } from './types'
import { useRevealedAxes } from './useRevealedAxes'

export interface BatchClassificationPanelProps {
	/** Pickable dimension axes, resolved from the registry by the page. */
	axes: PanelAxis[]
	/** The group-wide batch tuple (axis code → value code). */
	values: ClassificationTuple
	onChange: (axisCode: string, valueCode: string) => void
	/**
	 * Axes a file cannot commit without. Always shown, and never hidden behind
	 * the reveal — they are the difference between a batch that can be committed
	 * and one that cannot.
	 */
	requiredAxisCodes: string[]
	/**
	 * Axes shown unprompted even when empty and not required — see
	 * {@link useRevealedAxes}. They gate nothing; they just start open.
	 */
	defaultAxisCodes?: string[]
	/** Flip once a commit has been attempted — see {@link SelectField.showError}. */
	showErrors?: boolean
}

/**
 * Batch classification rail — a value applied to every staged file, refinable
 * per row.
 *
 * Follows the `FilterRail` pattern rather than listing every axis at once. The
 * registry has around thirteen dimensions; rendering them all made the panel
 * taller than the viewport and gave the three that actually gate a commit the
 * same weight as the ten that do not. Required axes are always present; the rest
 * are revealed on demand and stay visible once chosen, so a batch that genuinely
 * needs `channel` gets it without every batch paying for it.
 */
export function BatchClassificationPanel({
	axes,
	values,
	onChange,
	requiredAxisCodes,
	defaultAxisCodes = DEFAULT_AXES,
	showErrors = false,
}: BatchClassificationPanelProps) {
	const { shown, hidden, reveal } = useRevealedAxes(axes, requiredAxisCodes, values, defaultAxisCodes)
	const isRequired = (code: string) => requiredAxisCodes.includes(code)

	return (
		<CardShell as="aside" className="space-y-5">
			<div>
				<p className="text-xs font-medium uppercase tracking-widest text-ds-text-subtle">Batch classification</p>
				<p className="mt-1 text-ds-xs text-ds-text-muted">Applied to every file — refine any row individually.</p>
			</div>

			{shown.map((axis) => (
				<div key={axis.code} className="space-y-1">
					<AxisControl
						axis={axis}
						value={values[axis.code] ?? ''}
						onChange={(value) => onChange(axis.code, value)}
						// The table below repeats every axis label once per row, so the bare
						// label no longer identifies this control on its own.
						scope="all files"
						isRequired={isRequired(axis.code)}
						showError={showErrors}
					/>
				</div>
			))}

			<AddAxisButton hidden={hidden} onAdd={reveal} ariaContext="for all files" />
		</CardShell>
	)
}
